/**
 * Analytic Keplerian orbit propagation. Orbital elements -> position at time t.
 * Cheap, stable, drift-free (unlike numerically integrating planet orbits).
 *
 * Angles in radians. Distances in the sim's length unit (km in Black Marble).
 * Standard gravitational parameter GM of the PARENT body governs the period.
 */
import { vec3, type Vec3 } from './vec3.js';

export interface OrbitalElements {
  a: number; // semi-major axis
  e: number; // eccentricity (0 = circular)
  i: number; // inclination
  raan: number; // longitude of ascending node (Omega)
  argP: number; // argument of periapsis (omega)
  m0: number; // mean anomaly at epoch
  period: number; // orbital period (seconds); if <=0, derived from GM + a
}

/** Solve Kepler's equation M = E - e*sin(E) for eccentric anomaly E (Newton). */
export const solveEccentricAnomaly = (M: number, e: number, tol = 1e-10): number => {
  let E = e < 0.8 ? M : Math.PI;
  for (let iter = 0; iter < 50; iter++) {
    const f = E - e * Math.sin(E) - M;
    const fp = 1 - e * Math.cos(E);
    const dE = f / fp;
    E -= dE;
    if (Math.abs(dE) < tol) break;
  }
  return E;
};

const TWO_PI = Math.PI * 2;

/** Mean motion (rad/s). Prefers explicit period; else derives from GM and a. */
export const meanMotion = (el: OrbitalElements, gmParent: number): number => {
  if (el.period > 0) return TWO_PI / el.period;
  return Math.sqrt(gmParent / (el.a * el.a * el.a));
};

/**
 * Position relative to the parent body at simulation time `t` (seconds).
 * Returns a perifocal->inertial rotated Float64 vector.
 */
export const propagate = (el: OrbitalElements, gmParent: number, t: number): Vec3 => {
  const n = meanMotion(el, gmParent);
  const M = el.m0 + n * t;
  const E = solveEccentricAnomaly(((M % TWO_PI) + TWO_PI) % TWO_PI, el.e);

  // Perifocal-plane coordinates.
  const cosE = Math.cos(E);
  const sinE = Math.sin(E);
  const xPf = el.a * (cosE - el.e);
  const yPf = el.a * Math.sqrt(1 - el.e * el.e) * sinE;

  // Rotate perifocal -> inertial via argP (omega), inclination (i), raan (Omega).
  const co = Math.cos(el.argP);
  const so = Math.sin(el.argP);
  const ci = Math.cos(el.i);
  const si = Math.sin(el.i);
  const cO = Math.cos(el.raan);
  const sO = Math.sin(el.raan);

  const r11 = cO * co - sO * so * ci;
  const r12 = -cO * so - sO * co * ci;
  const r21 = sO * co + cO * so * ci;
  const r22 = -sO * so + cO * co * ci;
  const r31 = so * si;
  const r32 = co * si;

  return vec3(
    r11 * xPf + r12 * yPf,
    r21 * xPf + r22 * yPf,
    r31 * xPf + r32 * yPf,
  );
};
